import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, isoDate } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, useToast } from '../components/ui';

/**
 * §19.3 — a new salary record closes the previous one the day before it starts,
 * so nothing a past payroll already used is overwritten.
 *
 * It starts from the record in force — a raise usually changes one figure, not
 * nine — in that record's currency, or the base currency for someone's first.
 *
 * Route: `/salaries/:userId/new`.
 */
export function SalaryFormPage() {
  const { userId = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    basicSalary: 0,
    currency: '',
    standardDailyHours: 8,
    standardMonthlyDays: 22,
    overtimeMultiplier: 1.5,
    holidayMultiplier: 2,
    dayOffWorkMultiplier: 1.5,
    nightShiftAllowanceValue: 0,
    otherAllowances: 0,
    effectiveFrom: isoDate(),
  });
  const [prefilled, setPrefilled] = useState(false);

  const history = useQuery({
    queryKey: ['salaries', 'history', userId],
    queryFn: () => api.get<any>(`/payroll/salaries/${userId}`),
  });
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });

  useEffect(() => {
    if (prefilled || !history.data || !settings.data) return;
    const latest = history.data.profiles[0];
    const base = String(settings.data.finance?.['finance.baseCurrency'] ?? 'EGP');
    setForm((f) => ({
      ...f,
      ...(latest
        ? {
            basicSalary: Number(latest.basicSalary),
            currency: latest.currency,
            standardDailyHours: Number(latest.standardDailyHours ?? f.standardDailyHours),
            standardMonthlyDays: Number(latest.standardMonthlyDays ?? f.standardMonthlyDays),
            overtimeMultiplier: Number(latest.overtimeMultiplier ?? f.overtimeMultiplier),
            holidayMultiplier: Number(latest.holidayMultiplier ?? f.holidayMultiplier),
            dayOffWorkMultiplier: Number(latest.dayOffWorkMultiplier ?? f.dayOffWorkMultiplier),
            nightShiftAllowanceValue: Number(latest.nightShiftAllowanceValue ?? 0),
            otherAllowances: Number(latest.otherAllowances ?? 0),
          }
        : { currency: base }),
    }));
    setPrefilled(true);
  }, [prefilled, history.data, settings.data]);

  const save = useMutation({
    mutationFn: () => api.patch(`/users/${userId}/payroll-profile`, form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['salaries'] });
      navigate(`/salaries/${userId}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const employee = history.data?.employee;
  const name = employee ? localized(employee) : t('salaries.title');
  const number = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: Number(e.target.value) });

  // With no salary in force today, one that starts later leaves payroll and penalties nothing to go on until then.
  const today = isoDate();
  const day = (value: string | null | undefined) => (value ? String(value).slice(0, 10) : null);
  const inForceToday = ((history.data?.profiles ?? []) as any[]).some((p) => {
    const until = day(p.effectiveTo);
    return day(p.effectiveFrom)! <= today && (until === null || until >= today);
  });
  const startsLater = !!history.data && form.effectiveFrom > today && !inForceToday;

  return (
    <FormPage
      title={t('salaries.newRecord')}
      subtitle={employee ? `${name}${form.currency ? ` · ${form.currency}` : ''}` : undefined}
      backTo={`/salaries/${userId}`}
      backLabel={name}
      width="lg"
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={form.basicSalary <= 0 || !form.currency}
      loading={history.isLoading || settings.isLoading}
      error={history.error ?? settings.error}
      onRetry={() => void history.refetch()}
      onSubmit={() => save.mutate()}
    >
      <p className="mb-4 rounded-md border border-info/30 bg-info/8 px-3 py-2 text-body-sm text-info-fg dark:text-info-dark">
        {t('salaries.recordNote')}
      </p>
      <FormGrid>
        <Field label={t('employees.basicSalary')} required>
          <Input type="number" min={0} step="100" value={form.basicSalary} onChange={number('basicSalary')} autoFocus />
        </Field>
        <Field label={t('employees.effectiveFrom')} required>
          <Input
            type="date"
            value={form.effectiveFrom}
            onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
          />
          {startsLater && (
            <span className="mt-1.5 block rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-caption text-warning-fg dark:text-warning-dark">
              {t('salaries.startsLater', { date: formatDate(form.effectiveFrom, locale) })}
            </span>
          )}
        </Field>
        {/* Payroll's rate only since 2026-10-21: the hours a day owes are its shift's Standard hours. */}
        <Field label={t('salaries.dailyHours')} hint={t('salaries.dailyHoursHint')}>
          <Input type="number" step="0.5" value={form.standardDailyHours} onChange={number('standardDailyHours')} />
        </Field>
        <Field label={t('salaries.monthlyDays')}>
          <Input type="number" value={form.standardMonthlyDays} onChange={number('standardMonthlyDays')} />
        </Field>
        <Field label={`${t('payroll.overtime')} ×`}>
          <Input type="number" step="0.1" value={form.overtimeMultiplier} onChange={number('overtimeMultiplier')} />
        </Field>
        <Field label={`${t('payroll.holidayWork')} ×`}>
          <Input type="number" step="0.1" value={form.holidayMultiplier} onChange={number('holidayMultiplier')} />
        </Field>
        <Field label={`${t('payroll.dayOffWork')} ×`}>
          <Input type="number" step="0.1" value={form.dayOffWorkMultiplier} onChange={number('dayOffWorkMultiplier')} />
        </Field>
        <Field label={t('salaries.nightAllowancePerShift')}>
          <Input type="number" value={form.nightShiftAllowanceValue} onChange={number('nightShiftAllowanceValue')} />
        </Field>
        <Field label={t('payroll.otherAllowances')}>
          <Input type="number" value={form.otherAllowances} onChange={number('otherAllowances')} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
